import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { formatTimeAgo } from '../../utils/formatters';
import ReactDOM from 'react-dom';
import AuthModal from '../auth/AuthModal';
import { useUniversity } from '../../contexts/UniversityContext';

// Constantes para validaciones de longitud de comentarios
const MAX_COMMENT_LENGTH = 200;
const MIN_COMMENT_LENGTH = 1;

const CommentContainer = styled.div`
  margin-bottom: 10px;
  position: relative;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 6px;
  }
`;

const Comment = styled.div`
  display: flex;
  padding-top: ${props => props.$level === 0 ? '0' : props.$level === 1 ? '1px' : '2px'};
  width: 100%;
  box-sizing: border-box;
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1877f2, #4267B2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 8px;
  flex-shrink: 0;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    font-size: 13px;
    margin-right: 6px;
  }
  
  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
    font-size: 12px;
    margin-right: 5px;
  }
`;

// Simplificar el componente ThreadLine y hacerlo condicional
const ThreadLine = styled.div`
  position: absolute;
  width: 2px;
  background-color: #4CAF50;
  left: 14px;
  top: 28px;
  bottom: 0;
  z-index: 1;
  opacity: 0.4;
  display: ${props => props.$showLine ? 'block' : 'none'};
  transition: opacity 0.3s ease;
  
  @media (max-width: 768px) {
    left: 13px;
    top: 26px;
  }
  
  @media (max-width: 480px) {
    left: 12px;
    top: 24px;
    left: 13px;
    top: 26px;
  }
`;

const CommentContent = styled.div`
  flex-grow: 1;
  width: 100%;
  box-sizing: border-box;
`;

const CommentBubble = styled.div`
  background-color: ${props => props.theme.commentBg};
  padding: 8px 12px;
  border-radius: 16px;
  position: relative;
  display: inline-block;
  max-width: 100%;
  width: auto;
  box-sizing: border-box;
  border-left: 3px solid ${props => 
    props.$level === 0 ? 'transparent' : 
    props.$level === 1 ? '#4CAF5033' : '#FF980033'};
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 4px rgba(0,0,0,0.12);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 13px;
    border-radius: 12px;
  }
`;

const Username = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 2px;
  color: #1877f2;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const CommentText = styled.div`
  color: ${props => props.theme.text};
  word-break: break-word;
  font-size: 13px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 1.3;
  }
`;

const CommentActions = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
  margin-top: 4px;
  gap: 10px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    padding: 0 8px;
    gap: 8px;
    margin-top: 3px;
  }
  
  @media (max-width: 480px) {
    padding: 0 6px;
    gap: 6px;
    margin-top: 2px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.theme.primary};
    background-color: ${props => props.theme.buttonHover};
    text-decoration: none;
  }
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 3px 6px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
    padding: 2px 5px;
    border-radius: 8px;
  }
`;

const Timestamp = styled.span`
  color: ${props => props.theme.textTertiary};
  font-size: 11px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 9px;
  }
`;

// Añadir un ícono de envío para el formulario de respuestas
const SendButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 20px;
  padding: 0 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  transition: all 0.2s;
  
  &:hover {
    transform: scale(1.1);
    color: ${props => props.$hasText ? props.theme.primary : props.theme.textSecondary};
  }
  
  opacity: ${props => props.$hasText ? 1 : 0.5};
`;

const ReplyInput = styled.input`
  flex-grow: 1;
  border: 1px solid ${props => props.theme.divider};
  border-radius: 16px;
  padding: 6px 12px;
  outline: none;
  font-size: 13px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  
  @media (max-width: 768px) {
    padding: 5px 10px;
    font-size: 16px;
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    font-size: 16px;
    border-radius: 12px;
  }
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    font-size: 13px;
    
    @media (max-width: 768px) {
      font-size: 16px;
    }
    
    @media (max-width: 480px) {
      font-size: 16px;
    }
  }
`;

// Nuevo componente para mostrar el contador de caracteres
const CharacterCounter = styled.div`
  text-align: right;
  font-size: 10px;
  margin-top: 2px;
  color: ${props => props.$isNearLimit ? '#e74c3c' : props.theme.textTertiary};
  padding-right: 35px;
`;

// Modificar RepliesContainer para evitar la duplicación de líneas
const RepliesContainer = styled.div`
  margin-left: ${props => 20 + (props.$level * 4)}px;
  padding-left: ${props => 16 + (props.$level * 2)}px;
  position: relative;
  animation: slideDown 0.3s ease;
  width: calc(100% - ${props => 36 + (props.$level * 6)}px);
  box-sizing: border-box;
  
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 768px) {
    margin-left: ${props => 16 + (props.$level * 3)}px;
    padding-left: ${props => 14 + (props.$level * 2)}px;
    width: calc(100% - ${props => 30 + (props.$level * 5)}px);
  }
  
  @media (max-width: 480px) {
    margin-left: ${props => 12 + (props.$level * 2)}px;
    padding-left: ${props => 12 + (props.$level * 1)}px;
    width: calc(100% - ${props => 24 + (props.$level * 3)}px);
  }
`;

// Nueva constante para definir el nivel máximo de anidamiento
const MAX_REPLY_LEVEL = 3; // Permitir niveles 0, 1 y 2

// Agregar el componente SVG para la flecha
const ArrowIcon = styled.svg`
  width: 12px;
  height: 12px;
  transition: transform 0.3s ease;
  transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  margin-right: 4px;
  
  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
  }
`;

// Nuevo componente para el botón de mostrar/ocultar respuestas
const ToggleRepliesButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 12px;
  padding: 3px 10px;
  margin-top: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  border-radius: 12px;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: ${props => props.theme.buttonHover};
    transform: translateY(-1px);
  }
  
  // Ajustar el estilo para diferentes niveles
  font-size: ${props => props.$level === 1 ? '12px' : '12px'};
  padding: ${props => props.$level === 1 ? '2px 8px' : '3px 10px'};
  
  @media (max-width: 480px) {
    font-size: 11px;
    padding: 2px 6px;
    margin-top: 2px;
  }
  
  .replies-count {
    background-color: ${props => props.theme.primary}20;
    border-radius: 10px;
    padding: 1px 6px;
    margin-left: 4px;
    font-size: 11px;
    
    @media (max-width: 480px) {
      padding: 1px 4px;
      font-size: 10px;
    }
  }
`;

// Special styled component for rate limiting errors
const RateLimitError = styled.div`
  color: #d35400;
  font-size: 11px;
  margin: 4px 0 0 12px;
  padding: 4px 6px;
  background-color: rgba(241, 196, 15, 0.1);
  border-radius: 4px;
  border-left: 2px solid #f39c12;
  display: flex;
  align-items: center;
  
  .icon {
    margin-right: 4px;
    font-size: 12px;
  }
  
  .message {
    font-weight: 500;
  }
`;

const ReplyForm = styled.form`
  display: flex;
  margin-top: 6px;
  padding-left: 10px;
  align-items: center;
  transition: all 0.3s ease;
  animation: fadeIn 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  gap: 6px;
  
  @media (max-width: 768px) {
    padding-left: 8px;
    margin-top: 5px;
    gap: 5px;
  }
  
  @media (max-width: 480px) {
    padding-left: 6px;
    margin-top: 4px;
    gap: 4px;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const CommentItem = ({ comment, onReply, onAuthRequired, level = 0 }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplies, setShowReplies] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  const { isAuthenticated, user } = useAuth();
  const { currentUniversity } = useUniversity();
  
  // Verificar si el usuario puede comentar en esta universidad
  const canReply = isAuthenticated && (!user || user.university === currentUniversity?.id);
  
  // Detectar cambios en el tamaño de la pantalla
  useEffect(() => {
    // Esta función se mantiene para potencial uso futuro
    const handleResize = () => {
      // La funcionalidad relacionada con isMobile ha sido removida
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleReplyClick = () => {
    if (isAuthenticated) {
      if (user && user.university !== currentUniversity?.id) {
        setReplyError(`Solo puedes comentar en tu universidad: ${user.university.toUpperCase()}`);
        setTimeout(() => setReplyError(''), 3000);
        return;
      }
      setShowReplyForm(!showReplyForm);
    } else {
      // Show auth modal directly instead of notifying parent
      setShowAuthModal(true);
    }
  };
  
  const handleReplyChange = (e) => {
    // Si no está autenticado o no puede comentar, no procesar cambios
    if (!isAuthenticated || !canReply) {
      setShowAuthModal(true);
      return;
    }
    
    // Validar longitud máxima mientras el usuario escribe
    if (e.target.value.length <= MAX_COMMENT_LENGTH) {
      setReplyText(e.target.value);
      setReplyError('');
    }
  };
  
  const handleReplySubmit = async (e) => {
    e.preventDefault();
    const trimmedText = replyText.trim();
    
    // Validar longitud mínima
    if (trimmedText.length < MIN_COMMENT_LENGTH) {
      setReplyError('El comentario no puede estar vacío');
      return;
    }
    
    // Validar longitud máxima (doble verificación)
    if (trimmedText.length > MAX_COMMENT_LENGTH) {
      setReplyError(`El comentario no puede exceder los ${MAX_COMMENT_LENGTH} caracteres`);
      return;
    }
    
    if (isAuthenticated) {
      try {
        setIsSubmittingReply(true);
        await onReply(comment.id, trimmedText);
        setReplyText('');
        setReplyError('');
        setShowReplyForm(false);
        setShowReplies(true); // Mostrar respuestas después de agregar una nueva
      } catch (error) {
        // Handle rate limiting errors
        if (error.message && (
            error.message.includes('excedido el límite') || 
            error.message.includes('demasiado rápido')
        )) {
          setReplyError(error.message);
        } else if (error.response && error.response.status === 429) {
          // Error de límite de velocidad desde el servidor
          setReplyError(error.response.data || 'Has alcanzado el límite de comentarios. Inténtalo más tarde.');
        } else {
          setReplyError('Error al enviar la respuesta');
        }
      } finally {
        setIsSubmittingReply(false);
      }
    }
  };
  
  const toggleReplies = () => {
    setShowReplies(!showReplies);
  };
  
  // Determinar si el comentario tiene respuestas
  const hasReplies = comment.replies && Array.isArray(comment.replies) && comment.replies.length > 0;
  
  // Determinar si este comentario está en el nivel máximo de anidamiento (level comienza en 0)
  const isMaxLevel = level >= MAX_REPLY_LEVEL - 1; // Permitir hasta nivel 2
  
  const closeAuthModal = () => {
    setShowAuthModal(false);
  };
  
  return (
    <>
      <CommentContainer $level={level}>
        <Comment $hasReplies={hasReplies} $level={level}>
          <UserAvatar>
            🐸
          </UserAvatar>
          {/* Mostrar la línea del hilo solo cuando tiene respuestas y están visibles */}
          <ThreadLine $showLine={hasReplies && showReplies} $level={level} />
          
          <div style={{ width: '100%' }}>
            <CommentContent>
              {/* Añadir una indicación visual del nivel en el borde del comentario */}
              <CommentBubble $level={level}>
                <Username>
                  Universitario Anónimo
                </Username>
                <CommentText>{comment.text}</CommentText>
              </CommentBubble>
            </CommentContent>
            
            <CommentActions>
              {/* Solo mostrar el botón de responder si no estamos en el nivel máximo */}
              {!isMaxLevel && (
                <ActionButton onClick={handleReplyClick}>
                  Responder
                </ActionButton>
              )}
              <Timestamp>{formatTimeAgo(comment.created_at)}</Timestamp>
              
              {/* Botón para mostrar/ocultar respuestas si hay respuestas */}
              {hasReplies && (
                <ToggleRepliesButton 
                  onClick={toggleReplies} 
                  $level={level}
                  $expanded={showReplies}
                >
                  <ArrowIcon 
                    viewBox="0 0 24 24" 
                    $expanded={showReplies}
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </ArrowIcon>
                  {showReplies ? 'Ocultar' : 'Ver'}
                  {comment.replies.length > 0 && (
                    <span className="replies-count">
                      {comment.replies.length}
                    </span>
                  )}
                </ToggleRepliesButton>
              )}
            </CommentActions>
            
            {showReplyForm && !isMaxLevel && (
              <>
                <ReplyForm onSubmit={handleReplySubmit}>
                  <ReplyInput
                    type="text"
                    placeholder="Escribe una respuesta..."
                    value={replyText}
                    onChange={handleReplyChange}
                    autoFocus
                    maxLength={MAX_COMMENT_LENGTH}
                    disabled={isSubmittingReply || !canReply}
                    id={`reply-input-${comment.id}`}
                    name={`reply-input-${comment.id}`}
                    readOnly={!isAuthenticated || (user && user.university !== currentUniversity?.id)}
                  />
                  <SendButton 
                    type="submit" 
                    $hasText={replyText.trim().length > 0}
                    title="Enviar respuesta"
                    disabled={isSubmittingReply || !canReply || replyText.trim().length < MIN_COMMENT_LENGTH}
                  >
                    {isSubmittingReply ? (
                      <span>...</span>
                    ) : (
                      <span role="img" aria-label="enviar">➤</span>
                    )}
                  </SendButton>
                </ReplyForm>
                {replyError && (
                  replyError.includes('excedido el límite') || replyError.includes('demasiado rápido') || replyError.includes('alcanzado el límite') ? (
                    <RateLimitError>
                      <span className="icon">⏱️</span>
                      <span className="message">{replyError}</span>
                    </RateLimitError>
                  ) : (
                    <div style={{ color: '#e74c3c', fontSize: '11px', margin: '4px 0 0 12px' }}>{replyError}</div>
                  )
                )}
                {/* Mensaje de advertencia de universidad */}
                {isAuthenticated && user && user.university !== currentUniversity?.id && !replyError && (
                  <div style={{ color: '#e74c3c', fontSize: '11px', margin: '4px 0 0 12px' }}>
                    Solo puedes comentar en tu universidad: {user.university.toUpperCase()}
                  </div>
                )}
                {isSubmittingReply && (
                  <div style={{ fontSize: '11px', margin: '4px 0 0 12px', color: '#1877f2' }}>
                    Comentando...
                  </div>
                )}
                {!isSubmittingReply && replyText.length > MAX_COMMENT_LENGTH * 0.7 && (
                  <CharacterCounter $isNearLimit={replyText.length > MAX_COMMENT_LENGTH * 0.9}>
                    {replyText.length}/{MAX_COMMENT_LENGTH}
                  </CharacterCounter>
                )}
              </>
            )}
          </div>
        </Comment>
        
        {hasReplies && showReplies && (
          <RepliesContainer $level={level}>
            {comment.replies.map(reply => (
              <CommentItem
                key={reply.id}
                comment={reply}
                onReply={onReply}
                onAuthRequired={onAuthRequired}
                level={level + 1} // Incrementar el nivel para los comentarios anidados
              />
            ))}
          </RepliesContainer>
        )}
      </CommentContainer>
      {showAuthModal && 
        ReactDOM.createPortal(
          <AuthModal show={showAuthModal} onClose={closeAuthModal} initialTab="login" />,
          document.body
        )
      }
    </>
  );
};

export default CommentItem;
